// @ts-ignore - canvas-confetti types may not be properly resolved
import confetti from 'canvas-confetti'

export const triggerConfetti = (guessCount: number) => {
  switch (guessCount) {
    case 1:
      // COLOSSAL explosion for 1 guess - legendary achievement
      triggerColossalExplosion()
      break
    case 2:
    case 3:
      // MASSIVE multiple explosions for 2-3 guesses
      triggerMassiveMultipleExplosions(guessCount)
      break
    case 4:
      // BIG BIG BIG pause smaller pause x2 small pattern
      triggerBigPauseSmallPattern()
      break
    case 5:
      // Meh explosion - reduced particles
      triggerStandardExplosion(120)
      break
    case 6:
      // Small disappointing explosion
      triggerDisappointingExplosion()
      break
    default:
      triggerStandardExplosion(200)
  }
}

function triggerColossalExplosion() {
  const defaults = { origin: { y: 0.7 } }
  const count = 1000 // EVEN MORE MASSIVE particle count

  function fire(particleRatio: number, opts: any, delay = 0, useStars = false) {
    setTimeout(() => {
      // 50% stars, 50% regular confetti
      const starParticles = useStars
        ? Math.floor(count * particleRatio * 0.5)
        : 0
      const regularParticles = Math.floor(count * particleRatio * 0.5)

      if (starParticles > 0) {
        confetti({
          ...defaults,
          ...opts,
          particleCount: starParticles,
          shapes: ['star'],
          colors: ['FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8'],
        })
      }

      if (regularParticles > 0) {
        confetti({
          ...defaults,
          ...opts,
          particleCount: regularParticles,
        })
      }
    }, delay)
  }

  // Extended colossal explosion over 8 seconds for maximum satisfaction
  fire(0.5, { spread: 26, startVelocity: 70 }, 0, true)
  fire(0.4, { spread: 60, startVelocity: 65 }, 100, true)
  fire(0.6, { spread: 100, decay: 0.91, scalar: 0.8 }, 200, true)
  fire(
    0.3,
    { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 },
    300,
    true
  )
  fire(0.3, { spread: 120, startVelocity: 55 }, 400, true)

  // Additional massive bursts spread over time
  fire(0.4, { spread: 80, startVelocity: 50 }, 800, true)
  fire(0.3, { spread: 140, startVelocity: 40 }, 1200, true)
  fire(0.5, { spread: 60, startVelocity: 60 }, 1600, true)
  fire(0.4, { spread: 100, startVelocity: 45 }, 2000, true)
  fire(0.3, { spread: 120, startVelocity: 35 }, 2500, true)

  // Grand finale bursts
  fire(0.6, { spread: 80, startVelocity: 65 }, 3000, true)
  fire(0.5, { spread: 120, startVelocity: 50 }, 3500, true)
  fire(0.4, { spread: 100, startVelocity: 55 }, 4000, true)

  // Final celebration waves
  fire(0.3, { spread: 60, startVelocity: 40 }, 5000, true)
  fire(0.2, { spread: 80, startVelocity: 30 }, 6000, true)
  fire(0.1, { spread: 100, startVelocity: 25 }, 7000, true)
}

function triggerMassiveMultipleExplosions(guessCount: number) {
  const baseCount = guessCount === 3 ? 384 : 480 // 20% less for 3 guesses (480 * 0.8)

  function fireExplosionWithStars(
    starPercentage: number,
    particleCount: number = baseCount
  ) {
    const starParticles = Math.floor(particleCount * starPercentage)
    const regularParticles = particleCount - starParticles

    // Fire stars if any
    if (starParticles > 0) {
      triggerStandardExplosion(starParticles, {
        shapes: ['star'],
        colors: ['FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8'],
      })
    }

    // Fire regular confetti if any
    if (regularParticles > 0) {
      triggerStandardExplosion(regularParticles)
    }
  }

  // Determine star percentage based on guess count
  const starPercentage = guessCount === 2 ? 0.5 : guessCount === 3 ? 0.3 : 0

  if (guessCount === 3) {
    // For 3 guesses: Reduced main explosions + short shots at end
    for (let i = 0; i < 3; i++) {
      // Reduced from 5 to 3 main explosions
      setTimeout(() => {
        fireExplosionWithStars(starPercentage)
      }, i * 600)
    }

    // Short shots at the end
    setTimeout(() => {
      fireExplosionWithStars(starPercentage, 150) // Short shot
    }, 2500)

    setTimeout(() => {
      fireExplosionWithStars(starPercentage, 120) // Short shot
    }, 3200)
  } else {
    // For 2 guesses: Keep original pattern
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        fireExplosionWithStars(starPercentage)
      }, i * 500)
    }

    // Additional finale bursts
    setTimeout(() => {
      fireExplosionWithStars(starPercentage)
    }, 3000)

    setTimeout(() => {
      fireExplosionWithStars(starPercentage)
    }, 4500)
  }
}

function triggerBigPauseSmallPattern() {
  const defaults = { origin: { y: 0.7 } }

  // BIG explosion (35% less intensive)
  function bigExplosion() {
    const count = 290 // 35% less intensive (450 * 0.65)
    function fire(particleRatio: number, opts: any, delay = 0) {
      setTimeout(() => {
        confetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio),
        })
      }, delay)
    }

    fire(0.3, { spread: 26, startVelocity: 60 })
    fire(0.25, { spread: 60, startVelocity: 55 }, 50)
    fire(0.4, { spread: 100, decay: 0.91, scalar: 0.8 }, 100)
  }

  // Medium explosion (35% less intensive)
  function mediumExplosion() {
    const count = 130 // 35% less intensive (200 * 0.65)
    confetti({
      ...defaults,
      particleCount: count,
      spread: 70,
      startVelocity: 40,
    })
  }

  // Small explosion (35% less intensive)
  function smallExplosion() {
    const count = 65 // 35% less intensive (100 * 0.65)
    confetti({
      ...defaults,
      particleCount: count,
      spread: 50,
      startVelocity: 30,
    })
  }

  // Execute pattern with maximum 6 shots: BIG BIG BIG pause smaller pause small
  bigExplosion() // BIG at 0ms (1)
  setTimeout(() => bigExplosion(), 400) // BIG at 400ms (2)
  setTimeout(() => bigExplosion(), 800) // BIG at 800ms (3)

  setTimeout(() => mediumExplosion(), 1600) // smaller at 1600ms (4)

  setTimeout(() => smallExplosion(), 2700) // small at 2700ms (5)
  setTimeout(() => smallExplosion(), 3200) // final small at 3200ms (6) - reduced pause
}

function triggerStandardExplosion(count: number, shapeOptions?: any) {
  const defaults = { origin: { y: 0.7 } }

  function fire(particleRatio: number, opts: any, delay = 0) {
    setTimeout(() => {
      confetti({
        ...defaults,
        ...opts,
        ...shapeOptions, // Add shape options (stars/colors)
        particleCount: Math.floor(count * particleRatio),
      })
    }, delay)
  }

  fire(0.25, { spread: 26, startVelocity: 55 })
  fire(0.2, { spread: 60 }, 50)
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 }, 100)
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 }, 150)
  fire(0.1, { spread: 120, startVelocity: 45 }, 200)
}

function triggerDisappointingExplosion() {
  const defaults = { origin: { y: 0.7 } }

  // Small, weak explosion (20% more particles for visibility)
  confetti({
    ...defaults,
    particleCount: 36, // 30 * 1.2 = 36 (20% increase)
    spread: 40,
    startVelocity: 25,
    scalar: 0.6,
  })
}
